import { useState } from 'react';
import { useAuth } from '../providers/AuthProvider';
import { apiClient } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { getUserDisplayName } from '@tco/shared-types';
import { User, Lock, Mail, Shield } from 'lucide-react';

export function Profile() {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Validation errors
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!currentPassword) {
      errors.currentPassword = 'Текущий пароль обязателен';
    }

    if (!newPassword) {
      errors.newPassword = 'Новый пароль обязателен';
    } else if (newPassword.length < 6) {
      errors.newPassword = 'Пароль должен содержать минимум 6 символов';
    }

    if (!confirmPassword) {
      errors.confirmPassword = 'Подтверждение пароля обязательно';
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = 'Пароли не совпадают';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage('');
    setErrorMessage('');

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await apiClient.changePassword(currentPassword, newPassword);
      setSuccessMessage(response.message || 'Пароль успешно изменен');

      // Clear form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setValidationErrors({});
    } catch (error: any) {
      const message = error?.message || 'Не удалось изменить пароль';
      setErrorMessage(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Профиль</h1>
        <p className="text-muted-foreground">
          Управление настройками вашей учетной записи
        </p>
      </div>

      {/* User Info Card */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <User className="w-5 h-5" />
          Информация о пользователе
        </h2>

        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <User className="w-4 h-4 mt-1 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium text-muted-foreground">Имя</p>
              <p className="text-base">{getUserDisplayName(user)}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Mail className="w-4 h-4 mt-1 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium text-muted-foreground">Email</p>
              <p className="text-base">{user.email}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Shield className="w-4 h-4 mt-1 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium text-muted-foreground">Роль</p>
              <p className="text-base">{user.role?.name || 'Не указана'}</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Change Password Card */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Lock className="w-5 h-5" />
          Смена пароля
        </h2>

        {successMessage && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-800 rounded-lg">
            {successMessage}
          </div>
        )}

        {errorMessage && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 rounded-lg">
            {errorMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="currentPassword">Текущий пароль</Label>
            <Input
              id="currentPassword"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={isLoading}
              className={validationErrors.currentPassword ? 'border-red-500' : ''}
            />
            {validationErrors.currentPassword && (
              <p className="text-sm text-red-500">{validationErrors.currentPassword}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="newPassword">Новый пароль</Label>
            <Input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={isLoading}
              className={validationErrors.newPassword ? 'border-red-500' : ''}
            />
            {validationErrors.newPassword && (
              <p className="text-sm text-red-500">{validationErrors.newPassword}</p>
            )}
            <p className="text-sm text-muted-foreground">
              Минимум 6 символов
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Подтвердите новый пароль</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isLoading}
              className={validationErrors.confirmPassword ? 'border-red-500' : ''}
            />
            {validationErrors.confirmPassword && (
              <p className="text-sm text-red-500">{validationErrors.confirmPassword}</p>
            )}
          </div>

          <div className="pt-2">
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Сохранение...' : 'Изменить пароль'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
